import { SoftTag } from '../../Tag/SoftTag';
import { DANGER } from '../../Theme/palette';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { SharedForm } from '@components';
import { Button, Col, Form, FormInstance, Input, InputNumber, Popconfirm, Row } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React from 'react';
import { useRoleAccess, useView } from '@hooks';

interface IProps {
  formUpdate: FormInstance<any>;
  onUpdate: (index: number) => Promise<void>;
  onClear: (index: number) => Promise<void>;
  handleValuesChange: (changedValues: any, allValues: any) => void;
  isCardLoading: (index: number) => boolean;
  compact?: boolean;
}

const fieldLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  marginBottom: 2,
};

const readOnlyValue: React.CSSProperties = {
  fontSize: 13,
  padding: '4px 0',
  minHeight: 24,
};

const ReminderCard: React.FC<{
  name: number;
  index: number;
  restField: { fieldKey?: number | undefined };
  readOnly: boolean;
  cardLoading: boolean;
  isCompact: boolean;
  onUpdate: (index: number) => Promise<void>;
  onClear: (index: number) => Promise<void>;
  getFieldValue: (name: (string | number)[]) => any;
}> = ({ name, index, restField, readOnly, cardLoading, isCompact, onUpdate, onClear, getFieldValue }) => {
  const title = getFieldValue(['reminders', name, 'title']);
  const rawDate = getFieldValue(['reminders', name, 'dueDate']);
  const dueMileage = getFieldValue(['reminders', name, 'dueMileage']);
  const lastMod = getFieldValue(['reminders', name, 'lastModificationTime']);
  const dueDate = rawDate ? dayjs(rawDate, 'DD.MM.YYYY') : null;
  const isOverdue = rawDate && dueDate && dueDate.isValid() && dueDate.isBefore(dayjs(), 'day');

  return (
    <div
      style={{
        background: isOverdue ? DANGER.soft : '#fff',
        border: `1px solid ${isOverdue ? DANGER.card : '#f0f0f0'}`,
        borderRadius: 8,
        padding: isCompact ? '10px 14px' : '12px 16px',
      }}
    >
      {/* Hidden fields */}
      <Form.Item {...restField} name={[name, 'id']} hidden><Input /></Form.Item>
      <Form.Item {...restField} name={[name, 'reminderId']} hidden><Input /></Form.Item>

      {/* Header: title + overdue badge + actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          {isOverdue && (
            <SoftTag group="danger" style={{ margin: 0, fontSize: 11 }}>
              <ExclamationCircleOutlined /> Overdue
            </SoftTag>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            size="small"
            disabled={readOnly || cardLoading}
            loading={cardLoading}
            type="primary"
            onClick={() => onUpdate(index)}
          >
            Update
          </Button>
          <Popconfirm
            title="Clear reminder"
            description="Are you sure you want to remove the reminder?"
            onConfirm={() => onClear(index)}
            okText="Yes, clear"
            cancelText="Cancel"
          >
            <Button
              size="small"
              disabled={readOnly || cardLoading}
              loading={cardLoading}
              type="primary"
              danger
            >
              Clear
            </Button>
          </Popconfirm>
        </div>
      </div>

      {/* Fields */}
      <Row gutter={isCompact ? [12, 6] : [24, 8]}>
        <Col xs={12} sm={6} md={isCompact ? 12 : 5}>
          <div style={fieldLabel}>Interval months</div>
          <Form.Item
            {...restField}
            name={[name, 'months']}
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: 'Months is required' }]}
          >
            <InputNumber
              size="small"
              disabled={readOnly}
              placeholder="Months"
              style={{ width: '100%' }}
              controls={false}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6} md={isCompact ? 12 : 5}>
          <div style={fieldLabel}>Interval mileage</div>
          <Form.Item {...restField} name={[name, 'mileage']} style={{ marginBottom: 0 }}>
            <InputNumber
              size="small"
              disabled={readOnly}
              placeholder="Mileage"
              style={{ width: '100%' }}
              controls={false}
              min={0}
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6} md={isCompact ? 12 : 5}>
          <div style={fieldLabel}>Next due date</div>
          <div style={{ ...readOnlyValue, color: isOverdue ? DANGER.tag : undefined }}>
            {rawDate || '—'}
          </div>
        </Col>
        <Col xs={12} sm={6} md={isCompact ? 12 : 5}>
          <div style={fieldLabel}>Next due mileage</div>
          <div style={readOnlyValue}>{dueMileage ? Number(dueMileage).toLocaleString() : '—'}</div>
        </Col>
        {!isCompact && lastMod && (
          <Col xs={24} md={4}>
            <div style={fieldLabel}>Last updated</div>
            <div style={readOnlyValue}>{lastMod}</div>
          </Col>
        )}
      </Row>

      {isCompact && lastMod && (
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 6 }}>
          Last updated: {lastMod}
        </div>
      )}
    </div>
  );
};

const OrderVehicleReminder: React.FC<IProps> = ({
  formUpdate,
  onUpdate,
  onClear,
  handleValuesChange,
  isCardLoading,
  compact = false,
}) => {
  const { isMobile } = useView();
  const { order: access } = useRoleAccess();
  const isCompact = compact || isMobile;

  return (
    <SharedForm hideButtons name="UpdateForm" form={formUpdate} onValuesChange={handleValuesChange}>
      <Form.List name="reminders">
        {(fields) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fields.map(({ key, name, ...restField }, index) => (
              <Form.Item key={key} shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <ReminderCard
                    name={name}
                    index={index}
                    restField={restField}
                    readOnly={!access.editReminders}
                    cardLoading={isCardLoading(index)}
                    isCompact={isCompact}
                    onUpdate={onUpdate}
                    onClear={onClear}
                    getFieldValue={getFieldValue}
                  />
                )}
              </Form.Item>
            ))}
          </div>
        )}
      </Form.List>
    </SharedForm>
  );
};

export default observer(OrderVehicleReminder);
